import { FC, useEffect } from 'react';
import { toastr } from 'react-redux-toastr';
import { useIntl } from 'react-intl';
import { errorToast } from '../../utils/errorToast';
import { UserPermissionEnum } from '../../constants/enums';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useSendClinicInviteMutation } from '../../redux/api/clinic/clinicApi';
import Spinner from '../Shared/Loaders/Spinner';
import styles from './UsersTabForms.module.scss';
import Modal from '../Shared/Modal/Modal';
import cn from 'classnames';

interface FormValues {
  email: string;
  permission: UserPermissionEnum;
}

interface Props {
  isOpen: boolean;
  userGroupId: string;
  onDialogCloseClick: () => void;
}

const InviteUserForm: FC<Props> = ({ isOpen, userGroupId, onDialogCloseClick }) => {
  const intl = useIntl();
  const [inviteMember, { isLoading: isInviting }] = useSendClinicInviteMutation();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    defaultValues: {
      email: '',
      permission: UserPermissionEnum.USER,
    },
  });

  const onSubmit: SubmitHandler<FormValues> = async ({ email, permission }) => {
    const res = await inviteMember({
      email,
      permission,
      userGroupId,
    });
    if ('error' in res && 'data' in res.error) {
      const { modelState } = res.error.data;
      errorToast(intl, modelState);
      return;
    }
    toastr.success(
      intl.formatMessage({ id: 'general.success', defaultMessage: 'Success' }),
      intl.formatMessage({
        id: 'implant.inviteSuccess',
        defaultMessage: 'The invitation was sent successfully',
      }),
      { showCloseButton: false }
    );
    onDialogCloseClick();
    reset();
  };

  useEffect(() => {
    reset();
  }, [reset, onDialogCloseClick]);

  return (
    <Modal
      fullModal={true}
      header={intl.formatMessage({
        id: 'users.inviteUser',
        defaultMessage: 'Invite user',
      })}
      onDismiss={onDialogCloseClick}
      show={isOpen}
      modalContentClass={styles.modalContent}
    >
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.formInfo}>
          {intl.formatMessage({
            id: 'users.inviteUserFormInfo',
            defaultMessage:
              ' Invite a desired user by email and select permission. Click submit when you’re done.',
          })}
        </div>
        <div className={styles.formItem}>
          <label htmlFor="email">
            {intl.formatMessage({
              id: 'general.email',
              defaultMessage: 'Email',
            })}
          </label>
          <input
            type="email"
            id="email"
            disabled={isInviting}
            placeholder={intl.formatMessage({
              id: 'users.emailPlaceholder',
              defaultMessage: 'Enter email',
            })}
            className={`${errors.email && styles.error}`}
            {...register('email', { required: 'Please enter email' })}
          />
          {errors.email && <p className={styles.helperText}>{errors.email.message}</p>}
        </div>
        <div className={styles.formItem}>
          <label htmlFor="userPermission">
            {intl.formatMessage({
              id: 'general.userPermission',
              defaultMessage: 'User permission',
            })}
          </label>
          <select
            id="userPermission"
            disabled={isInviting}
            placeholder={intl.formatMessage({
              id: 'users.userPermissionPlaceholder',
              defaultMessage: 'Select user permission',
            })}
            className={`${errors.permission && styles.error}`}
            {...register('permission', { required: 'Please select user permission' })}
          >
            <option value={UserPermissionEnum.USER}>
              {intl.formatMessage({
                id: 'general.user',
                defaultMessage: 'User',
              })}
            </option>
            <option value={UserPermissionEnum.ADMIN}>
              {intl.formatMessage({
                id: 'general.admin',
                defaultMessage: 'Admin',
              })}
            </option>
          </select>
          {errors.permission && <p className={styles.helperText}>{errors.permission.message}</p>}
        </div>
        <div className={styles.formAction}>
          <button
            type="button"
            disabled={isInviting}
            className={cn(styles.btn, styles.btnOutline)}
            onClick={onDialogCloseClick}
          >
            {intl.formatMessage({ id: 'general.cancel', defaultMessage: 'Cancel' })}
          </button>
          <button type="submit" className={cn(styles.btn, styles.btnPrimary)} disabled={isInviting}>
            {isInviting ? (
              <Spinner />
            ) : (
              intl.formatMessage({ id: 'general.save', defaultMessage: 'Save' })
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default InviteUserForm;
